import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { bindActionCreators } from 'redux'

import * as ActionsMapping from '../../features/store/actions'
import { reducers } from '../../features/store/reducers'

import mockerApi from '../api/mocker'

import { useAppLoader } from '../hooks/AppLoader'
import { useAppSnackbars } from '../hooks/AppSnackbars'

import { getEnviron, ucfirst } from '../utils'

import Config from '../../features/config.json'
import MockData from '../../features/mock.json'

const DEFAULT_DATA_CHECK_TIMER = 30000

export const useAppData = () => {
    const store = useStore()

    const actions = useRef({})
    const states = useRef({})
    const environ = useRef(getEnviron())
    const now = useRef(new Date())
    const timerReloadData = useRef()
    const duration = useRef(3000)

    const { show: showLoader } = useAppLoader()
    const { stack: showSnackbar } = useAppSnackbars()

    useEffect(() => {
        const showReducerLoader = Config.interface?.showReducerLoader ?? false
        if (showReducerLoader) showLoader('LOADING')

        loadActions()
        loadData()
        timerReloadData.current = setInterval(loadData, DEFAULT_DATA_CHECK_TIMER)
        return () => {
            clearInterval(timerReloadData.current)
        }
    }, [])

    const loadActions = () => {
        for (let reducer in reducers) {
            actions.current[reducer] = bindActionCreators(
                ActionsMapping[ucfirst(reducer)], store.dispatch
            )
        }
    }

    const loadData = () => {
        now.current = new Date()
        states.current = store.getState()
        if (Config[environ.current]?.useMock) loadFromMock()
        else if (Config[environ.current]?.mocker) loadFromMocker()
        else loadFromApi()
    }

    const loadFromMock = () => {
        for (let reducer in reducers) {
            if (MockData[reducer]) {
                let data = MockData[reducer].data
                if (data && actions.current[reducer].populate
                    && typeof actions.current[reducer].populate === 'function'
                    && reducer !== 'app') process(reducer, data)
            }
        }
    }

    const loadFromMocker = () => {
        for (let reducer in reducers) {
            let useNavigationApi = Config.interface?.navigation?.fromApi ?? false
            let useNavigationMock = Config.interface?.navigation?.useMock ?? false

            if (reducer === 'navigation' && useNavigationMock) {
                let data = MockData[reducer].data
                if (data && actions.current[reducer].populate
                    && typeof actions.current[reducer].populate === 'function')
                    process(reducer, data)
            }

            if (reducer === 'navigation' && !useNavigationApi) continue

            if (reducer !== 'app')
                process(reducer, null, true)
        }
    }

    const loadFromApi = () => {
        for (let reducer in reducers) {
            let useNavigationApi = Config.interface?.navigation?.fromApi ?? false
            if (reducer === 'navigation' && !useNavigationApi) continue
            if (actions.current[reducer].load
                && typeof actions.current[reducer].load === 'function'
                && reducer !== 'app') process(reducer)
        }
    }

    const process = (reducer, mockData = null, useMocker = false) => {
        let lastUpdate = (states.current[reducer].lastUpdate !== null)
            ? new Date(states.current[reducer].lastUpdate) : null

        let reducerConfig = Config[environ.current].reducers ?? {}
        let timerConfig = Config[environ.current].timer ?? {}
        let timer = reducerConfig[reducer]?.timer ?? timerConfig.dataCheck ?? DEFAULT_DATA_CHECK_TIMER
        let reload = reducerConfig[reducer]?.reload ?? false
        let notAtStart = reducerConfig[reducer]?.notAtStart ?? false

        if (!notAtStart && (!lastUpdate || ((now.current.getTime() - lastUpdate.getTime() > timer) && reload))) {
            if (mockData) actions.current[reducer].populate(mockData, {
                success: (reducerName) => { loadDataSuccess(reducerName) },
                error: (reducerName) => { loadDataError(reducerName) }
            })
            else if (useMocker) {
                let extension = reducerConfig[reducer]?.extension
                if (!extension) extension = '.json'
                else if (extension.toLowerCase() === 'php') extension = ''
                mockerApi
                    .load(`${reducer}/${reducer}${extension}`)
                    .then(result => {
                        const callback = (result.except || result.error)
                            ? (reducerName) => { loadDataError(result, reducerName) }
                            : (reducerName) => { loadDataSuccess(reducerName) }

                        actions.current[reducer].populate(result.data, callback)
                    })
            }
            else actions.current[reducer].load(
                (reducerName) => { loadDataSuccess(reducerName) },
                (result, reducerName) => { loadDataError(result, reducerName) }
            )

            actions.current[reducer].setLastUpdate(now.current.getTime())
        }
    }

    const loadDataSuccess = (reducerName) => {
        actions.current['app'].setLoaders(reducerName, { populated: 1 })
    }

    const loadDataError = (result, reducerName) => {
        showSnackbar(reducerName, `Error on loading: ${reducerName}`, 'warning', duration.current)
        duration.current =  duration.current + 250
        actions.current['app'].setLoaders(reducerName, { populated: 2 })
    }
}